import React, { useCallback, useEffect, useRef, useState } from 'react';

import PropTypes from 'prop-types';

import _get from 'lodash/get';

import { useDevice } from '@bonnet/next/device';

import { Button } from 'reaxl';

import SpecItem from './SpecItem';

/**
 * Content container that handles expanding/collapsing layout for desktop
 */
const ContentContainer = React.memo(({ children, isExpanded, textRef, isMobile }) => {
    // Extract className logic to avoid nested ternary
    const getContainerClassName = () => {
        if (isMobile) {
            return 'iris:flex iris:flex-col iris:gap-4 margin-4';
        }

        const baseClasses = 'flex-grow-1 iris:flex min-width-0 iris:gap-4 margin-4';
        const expandedClasses = isExpanded
            ? 'flex-wrap align-items-start'
            : 'text-nowrap overflow-hidden align-items-center';

        return `${baseClasses} ${expandedClasses}`;
    };

    return (
        <div
            ref={textRef}
            className={getContainerClassName()}
        >
            {children}
        </div>
    );
});

/**
 * Overflow controls component showing ellipsis and "more" button (desktop only)
 */
const OverflowControls = React.memo(({ onExpand }) => (
    <div
        className="iris:flex iris:items-center"
        style={{ marginLeft: '-1rem' }}
    >
        <span>...</span>
        <Button
            bsStyle="text"
            className="flex-shrink-0 padding-left-1 padding-right-4"
            onClick={onExpand}
        >
            more
        </Button>
    </div>
));

/**
 * Unified component that displays vehicle specifications for both mobile and desktop layouts
 * Handles responsive behavior and expandable functionality automatically
 */
function VehicleFeatureItem({ availableSpecs, exteriorColorData, interiorColorData }) {
    const device = useDevice();
    const isMobile = _get(device, 'is.xs', false);

    const [isExpanded, setIsExpanded] = useState(false);
    const [isOverflowing, setIsOverflowing] = useState(false);
    const textRef = useRef(null);

    // Check if text overflows when not expanded (desktop only)
    useEffect(() => {
        if (!isMobile && textRef.current && !isExpanded) {
            const element = textRef.current;
            setIsOverflowing(element.scrollWidth > element.clientWidth);
        }
    }, [isExpanded, availableSpecs, isMobile]);

    const handleExpand = useCallback(() => {
        setIsExpanded(true);
    }, []);

    // Render spec items
    const specItems = availableSpecs.map((spec, index) => (
        <SpecItem
            key={`${spec.key}-${index}`}
            spec={spec}
            exteriorColorData={exteriorColorData}
            interiorColorData={interiorColorData}
        />
    ));

    // Mobile layout - simple column
    if (isMobile) {
        return (
            <ContentContainer isMobile>
                {specItems}
            </ContentContainer>
        );
    }

    // Desktop layout - with expansion functionality
    return (
        <div className="iris:flex iris:items-center w-100">
            <ContentContainer
                isExpanded={isExpanded}
                textRef={textRef}
                isMobile={false}
            >
                {specItems}
            </ContentContainer>
            {isOverflowing && !isExpanded && (
                <OverflowControls onExpand={handleExpand} />
            )}
        </div>
    );
}

VehicleFeatureItem.propTypes = {
    availableSpecs: PropTypes.array.isRequired,
    exteriorColorData: PropTypes.object,
    interiorColorData: PropTypes.object,
};

export default VehicleFeatureItem;
